import { useMemo, useState } from 'react'
import { Link } from 'react-router-dom'
import { useConfigurationGraph, usePeriods } from '../../api/hooks'
import {
  ConfigurationGraphView,
  type EmphasisFocus,
} from '../../components/ConfigurationGraphView'
import { formatPercent } from '../../components/Badges'

/**
 * How the configuration fits together: accounts → drivers → cost centers.
 *
 * @remarks
 * Every other configuration screen answers for one kind of thing. The cost center tree
 * knows its own hierarchy and nothing about which drivers reach it; the driver list knows
 * its percentages and nothing about which accounts arrive; the mapping screen works one
 * account at a time. Each is the right shape for editing that thing, and between them
 * there was no answer to "what does this configuration actually do".
 *
 * The two failures this exists to make visible are both invisible on every other screen,
 * because both of them let every other check pass:
 *
 *   - an ACCOUNT that routes nowhere. Its balance is allocated nowhere, and the run still
 *     reconciles — zero allocated against zero routed does balance. QC-A001 blocks it;
 *     here it is a node with no line leaving it.
 *
 *   - a COST CENTER no driver reaches. Every driver still totals 100%, every account is
 *     still fully mapped, the run completes — and the department is absent from every
 *     report. QC-C006 warns; here it is a node with no line arriving.
 *
 * This screen never writes. Each thing it draws is edited on the screen that owns it, and
 * a second way to change a mapping would be a second set of rules about what a valid
 * mapping is.
 */
export function ConfigurationOverview() {
  const { data: periods = [] } = usePeriods()

  /*
      Null means "as at today", which is what the driver list already shows.

      Naming a period instead resolves every effective-dated edge against that period's
      END DATE — the date the engine itself uses (§14) — so the question becomes "what
      would a run against this period do". The two answers differ exactly when somebody
      has dated a change forward, which is the case worth being able to look at before
      the close rather than after it.
  */
  const [periodId, setPeriodId] = useState<number | null>(null)
  const [selected, setSelected] = useState<EmphasisFocus | null>(null)
  const [showOnlyProblems, setShowOnlyProblems] = useState(false)
  const [asTable, setAsTable] = useState(false)

  const { data: graph, isLoading, error } = useConfigurationGraph(periodId)

  // Local midnight, formatted the way the API returns a DateOnly, so the two read alike.
  const today = useMemo(() => {
    const now = new Date()
    return [
      now.getFullYear(),
      String(now.getMonth() + 1).padStart(2, '0'),
      String(now.getDate()).padStart(2, '0'),
    ].join('-')
  }, [])

  const visibleCostCenterIds = useMemo(() => {
    if (!graph) return new Set<number>()

    if (!showOnlyProblems) {
      return new Set(graph.costCenters.map((c) => c.costCenterId))
    }

    /*
        Uncovered nodes, plus the headings above them.

        Filtering to the problems alone would list four codes with no indication of where
        in the chart they sit, and "Physical Therapy receives nothing" means something
        different depending on whether its siblings do.
    */
    const keep = new Set<number>()
    const byId = new Map(graph.costCenters.map((c) => [c.costCenterId, c]))

    for (const centre of graph.costCenters) {
      if (!centre.hasNoCoverage) continue

      keep.add(centre.costCenterId)

      let parentId = centre.parentCostCenterId
      while (parentId !== null && !keep.has(parentId)) {
        keep.add(parentId)
        parentId = byId.get(parentId)?.parentCostCenterId ?? null
      }
    }

    return keep
  }, [graph, showOnlyProblems])

  if (isLoading) {
    return <p className="px-4 py-3 text-[12px]">Loading…</p>
  }

  if (error || !graph) {
    return (
      <p className="px-4 py-3 text-[12px]" style={{ color: 'var(--color-error)' }}>
        Could not read the configuration. {error instanceof Error ? error.message : ''}
      </p>
    )
  }

  const uncovered = graph.costCenters.filter((c) => c.hasNoCoverage)
  const brokenDrivers = graph.drivers.filter(
    (d) =>
      d.status !== 'Retired' &&
      (d.healthState === 'Incomplete' || d.healthState === 'NoAllocations'),
  )
  const unusedDrivers = graph.drivers.filter(
    (d) => d.status === 'Active' && d.accountMapCount === 0,
  )

  const { unroutedCount, incompleteCount, totalAllocableAccounts, problemAccountsTruncated } =
    graph.accountTotals

  const clean =
    unroutedCount === 0 &&
    incompleteCount === 0 &&
    uncovered.length === 0 &&
    brokenDrivers.length === 0

  return (
    <div className="flex h-full min-h-0 flex-col">
      {/* ------------------------------------------------------------- controls */}
      <div
        className="flex shrink-0 flex-wrap items-center gap-3 border-b px-4 py-2"
        style={{ borderColor: 'var(--color-rule)' }}
      >
        <h2 className="text-[13px] font-medium" style={{ color: 'var(--color-ink)' }}>
          Configuration overview
        </h2>

        <label className="flex items-center gap-1.5 text-[12px]">
          <span style={{ color: 'var(--color-muted)' }}>As at</span>
          <select
            className="input"
            style={{ width: 'auto' }}
            value={periodId ?? ''}
            onChange={(e) => setPeriodId(e.target.value === '' ? null : Number(e.target.value))}
          >
            {/*
                The client's today, not graph.asOf.

                graph.asOf is the date the LOADED graph was resolved against — so once a
                period is selected it is that period's end date, and the option that means
                "today" was reading "Today (2026-07-31)". An option that misreports the
                thing it switches to is worse than one with no date on it at all.
            */}
            <option value="">Today ({today})</option>
            {periods.map((p) => (
              <option key={p.periodId} value={p.periodId}>
                {p.periodCode} — as the engine would resolve it
              </option>
            ))}
          </select>
        </label>

        <label className="flex items-center gap-1.5 text-[12px]">
          <input
            type="checkbox"
            checked={showOnlyProblems}
            onChange={(e) => setShowOnlyProblems(e.target.checked)}
          />
          <span style={{ color: 'var(--color-muted)' }}>Only cost centers with no coverage</span>
        </label>

        <div className="ml-auto flex gap-1">
          {/*
              The table is not a fallback for a broken chart. It is how the same content
              is read by somebody using a screen reader, and how a value gets copied into
              an email — a tooltip is the one place a number must never live alone.
          */}
          <button
            type="button"
            className={asTable ? 'btn btn-sm btn-primary' : 'btn btn-sm'}
            onClick={() => setAsTable(true)}
            aria-pressed={asTable}
          >
            Table
          </button>
          <button
            type="button"
            className={asTable ? 'btn btn-sm' : 'btn btn-sm btn-primary'}
            onClick={() => setAsTable(false)}
            aria-pressed={!asTable}
          >
            Diagram
          </button>
        </div>
      </div>

      {/* -------------------------------------------------------------- summary */}
      <div
        className="flex shrink-0 flex-wrap items-center gap-x-5 gap-y-1 border-b px-4 py-2 text-[12px]"
        style={{ borderColor: 'var(--color-rule)', background: 'var(--color-panel-alt)' }}
      >
        <span style={{ color: 'var(--color-muted)' }}>
          {graph.drivers.length} driver{graph.drivers.length === 1 ? '' : 's'} ·{' '}
          {totalAllocableAccounts} allocable account{totalAllocableAccounts === 1 ? '' : 's'} ·{' '}
          {graph.costCenters.filter((c) => c.isAllocable).length} receiving cost center
          {graph.costCenters.filter((c) => c.isAllocable).length === 1 ? '' : 's'}
        </span>

        {clean && (
          <span style={{ color: 'var(--color-ok)' }}>
            ✓ Every account routes somewhere and every receiving cost center is targeted.
          </span>
        )}

        {/* Each problem says its rule code, so this screen and the QC dashboard cannot
            describe the same fault two different ways. */}
        {unroutedCount > 0 && (
          <Problem severity="error">
            {unroutedCount} account{unroutedCount === 1 ? '' : 's'} route nowhere (QC-A001)
          </Problem>
        )}
        {incompleteCount > 0 && (
          <Problem severity="error">
            {incompleteCount} account{incompleteCount === 1 ? '' : 's'} do not total 100 (QC-A002)
          </Problem>
        )}
        {brokenDrivers.length > 0 && (
          <Problem severity="error">
            {brokenDrivers.length} driver{brokenDrivers.length === 1 ? '' : 's'} unusable
            (QC-D001/D002)
          </Problem>
        )}
        {uncovered.length > 0 && (
          <Problem severity="warn">
            {uncovered.length} cost center{uncovered.length === 1 ? '' : 's'} receive nothing
            (QC-C006)
          </Problem>
        )}
        {unusedDrivers.length > 0 && (
          <Problem severity="warn">
            {unusedDrivers.length} driver{unusedDrivers.length === 1 ? '' : 's'} unused (QC-D014)
          </Problem>
        )}

        {problemAccountsTruncated && (
          <span style={{ color: 'var(--color-faint)' }}>
            Showing the first {graph.problemAccounts.length} problem accounts.
          </span>
        )}

        {graph.periodCode && (
          <span style={{ color: 'var(--color-faint)' }}>
            Resolved against {graph.periodCode}’s end date, {graph.asOf}.
          </span>
        )}
      </div>

      {/* ---------------------------------------------------------------- body */}
      {asTable ? (
        <GraphTable graph={graph} />
      ) : (
        <>
          <div className="min-h-0 flex-1 overflow-auto">
            <ConfigurationGraphView
              graph={graph}
              costCenterIds={visibleCostCenterIds}
              selected={selected}
              onSelect={setSelected}
            />
          </div>

          <Legend />
        </>
      )}
    </div>
  )
}

function Problem({
  severity,
  children,
}: {
  severity: 'error' | 'warn'
  children: React.ReactNode
}) {
  return (
    <span
      className="flex items-center gap-1"
      style={{ color: severity === 'error' ? 'var(--color-error)' : 'var(--color-warn)' }}
    >
      {/* The glyph, so the state never rests on colour alone. */}
      <span aria-hidden="true" style={{ fontWeight: 700 }}>
        !
      </span>
      {children}
    </span>
  )
}

/**
 * Always present, not conditional on series count.
 *
 * There are only three things colour means here and the diagram is unreadable without
 * knowing them — particularly that a pale box is normal and a coloured one is a fault,
 * which is the opposite of the usual convention that colour means "important".
 */
function Legend() {
  return (
    <div
      className="flex shrink-0 flex-wrap items-center gap-x-4 gap-y-1 border-t px-4 py-1.5 text-[11px]"
      style={{ borderColor: 'var(--color-rule)', color: 'var(--color-muted)' }}
    >
      <Swatch fill="var(--color-panel)" stroke="var(--color-rule)" label="Fine" />
      <Swatch
        fill="var(--color-error-wash)"
        stroke="var(--color-error)"
        label="Blocks a run — QC error"
        glyph
      />
      <Swatch
        fill="var(--color-warn-wash)"
        stroke="var(--color-warn)"
        label="Worth a look — QC warning"
        glyph
      />
      <Swatch
        fill="var(--color-accent-wash)"
        stroke="var(--color-accent)"
        label="Selected, and what it connects to"
      />
      <span>Hover or focus anything to trace it. Scroll to zoom, drag to pan.</span>
    </div>
  )
}

function Swatch({
  fill,
  stroke,
  label,
  glyph = false,
}: {
  fill: string
  stroke: string
  label: string
  glyph?: boolean
}) {
  return (
    <span className="flex items-center gap-1.5">
      <span
        style={{
          display: 'inline-flex',
          alignItems: 'center',
          justifyContent: 'center',
          width: 22,
          height: 13,
          borderRadius: 4,
          background: fill,
          border: `1px solid ${stroke}`,
          color: stroke,
          fontSize: 10,
          fontWeight: 700,
        }}
        aria-hidden="true"
      >
        {glyph ? '!' : ''}
      </span>
      {label}
    </span>
  )
}

/**
 * The same graph as rows.
 *
 * Not a downgrade of the diagram — the diagram's job is showing shape at a glance, and a
 * table's is being read exactly, sorted, and copied. Every edge and every percentage the
 * diagram puts in a tooltip is here in text.
 */
function GraphTable({ graph }: { graph: ReturnType<typeof useConfigurationGraph>['data'] }) {
  if (!graph) return null

  const centreById = new Map(graph.costCenters.map((c) => [c.costCenterId, c]))
  const accountEdgeById = new Map(graph.accountEdges.map((e) => [e.costDriverId, e]))

  return (
    <div className="min-h-0 flex-1 overflow-auto px-4 py-3">
      {graph.problemAccounts.length > 0 && (
        <>
          <h3 className="col-head mb-1">accounts that will not allocate</h3>
          <table className="mb-5 w-full text-[12px]">
            <thead>
              <tr style={{ color: 'var(--color-faint)' }}>
                <th className="px-2 py-1 text-left font-medium">Account</th>
                <th className="px-2 py-1 text-left font-medium">Name</th>
                <th className="px-2 py-1 text-right font-medium">Routed</th>
                <th className="px-2 py-1 text-left font-medium">Problem</th>
              </tr>
            </thead>
            <tbody>
              {graph.problemAccounts.map((a) => (
                <tr key={a.accountId} className="grid-row">
                  <td className="ident px-2 py-1">{a.accountNumber}</td>
                  <td className="px-2 py-1">{a.name}</td>
                  <td className="num px-2 py-1">{formatPercent(a.totalPercent)}</td>
                  <td className="px-2 py-1" style={{ color: 'var(--color-error)' }}>
                    {a.problem === 'Unrouted'
                      ? 'Routes through no driver (QC-A001)'
                      : 'Does not total 100 (QC-A002)'}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </>
      )}

      <h3 className="col-head mb-1">every edge</h3>
      <table className="w-full text-[12px]">
        <thead>
          <tr style={{ color: 'var(--color-faint)' }}>
            <th className="px-2 py-1 text-left font-medium">Driver</th>
            <th className="px-2 py-1 text-right font-medium">Accounts in</th>
            <th className="px-2 py-1 text-left font-medium">Cost center out</th>
            <th className="px-2 py-1 text-right font-medium">Share</th>
          </tr>
        </thead>
        <tbody>
          {graph.drivers.flatMap((driver) => {
            const edges = graph.driverEdges.filter((e) => e.costDriverId === driver.costDriverId)
            const accounts = accountEdgeById.get(driver.costDriverId)?.accountCount ?? 0

            if (edges.length === 0) {
              return (
                <tr key={`d-${driver.costDriverId}`} className="grid-row">
                  <td className="px-2 py-1">{driver.name}</td>
                  <td className="num px-2 py-1">{accounts}</td>
                  <td className="px-2 py-1" style={{ color: 'var(--color-error)' }}>
                    No effective allocations (QC-D002)
                  </td>
                  <td className="num px-2 py-1">—</td>
                </tr>
              )
            }

            return edges.map((edge, index) => {
              const centre = centreById.get(edge.costCenterId)
              return (
                <tr key={`e-${edge.costDriverId}-${edge.costCenterId}`} className="grid-row">
                  {/* The driver name once per group: repeating it on every row is noise
                      that makes the grouping harder to see, not easier. */}
                  <td className="px-2 py-1">{index === 0 ? driver.name : ''}</td>
                  <td className="num px-2 py-1">{index === 0 ? accounts : ''}</td>
                  <td className="px-2 py-1">
                    <span className="ident">{centre?.code}</span> {centre?.name}
                  </td>
                  <td className="num px-2 py-1">{formatPercent(edge.percent)}</td>
                </tr>
              )
            })
          })}
        </tbody>
      </table>

      {graph.costCenters.some((c) => c.hasNoCoverage) && (
        <>
          <h3 className="col-head mb-1 mt-5">cost centers no driver reaches</h3>
          <table className="w-full text-[12px]">
            <tbody>
              {graph.costCenters
                .filter((c) => c.hasNoCoverage)
                .map((c) => (
                  <tr key={c.costCenterId} className="grid-row">
                    <td className="ident px-2 py-1">{c.code}</td>
                    <td className="px-2 py-1">{c.name}</td>
                    <td className="px-2 py-1" style={{ color: 'var(--color-warn)' }}>
                      Allocable, and nothing targets it. It will receive nothing (QC-C006).
                    </td>
                    <td className="px-2 py-1">
                      <Link to="/drivers" style={{ color: 'var(--color-accent)' }}>
                        Edit a driver
                      </Link>
                    </td>
                  </tr>
                ))}
            </tbody>
          </table>
        </>
      )}
    </div>
  )
}
